import type { AllFees, CostsAndFees, Fee, FeeSection } from '@/src/types/costAndFees.type';

export type CostSelection = {
  selectedBedCount: number | null;
  selectedUnitZpid: string | null;
};

export function mergeFees(baseFees: AllFees, overrides: AllFees): AllFees {
  return {
    monthly: mergeFeeSection(baseFees.monthly, overrides.monthly),
    moveIn: mergeFeeSection(baseFees.moveIn, overrides.moveIn),
    whenUsed: mergeFeeSection(baseFees.whenUsed, overrides.whenUsed),
  };
}

function mergeFeeSection(base: FeeSection, override: FeeSection): FeeSection {
  const overrideGroups = override.feeGroup.filter((group) => group.fees.length > 0);
  if (overrideGroups.length === 0) return base;

  const feeGroups = base.feeGroup.map((group) => ({ ...group, fees: [...group.fees] }));

  for (const overrideGroup of overrideGroups) {
    const groupIndex = feeGroups.findIndex((group) => group.type === overrideGroup.type);
    if (groupIndex === -1) {
      feeGroups.push({ ...overrideGroup, fees: [...overrideGroup.fees] });
      continue;
    }

    const feeMap = new Map<string, Fee>(
      feeGroups[groupIndex].fees.map((fee) => [`${fee.type}-${fee.displayName}`, fee]),
    );
    for (const fee of overrideGroup.fees) {
      feeMap.set(`${fee.type}-${fee.displayName}`, fee);
    }
    feeGroups[groupIndex] = { ...feeGroups[groupIndex], fees: Array.from(feeMap.values()) };
  }

  return {
    ...base,
    displayName: override.displayName ?? base.displayName,
    feeGroup: feeGroups,
  };
}

export function getEffectiveCostsAndFees(costsData: CostsAndFees, selection: CostSelection) {
  if (selection.selectedUnitZpid) {
    const unitSelector = costsData.selectors.units.find((selector) => selector.unitZpid === selection.selectedUnitZpid);
    if (unitSelector) {
      return {
        effectiveCosts: unitSelector.selector.costs,
        effectiveFees: mergeFees(costsData.fees, unitSelector.selector.feeOverrides),
      };
    }
  }

  if (selection.selectedBedCount !== null) {
    const bedSelector = costsData.selectors.beds.find((selector) => selector.numBeds === selection.selectedBedCount);
    if (bedSelector) {
      return {
        effectiveCosts: bedSelector.selector.costs,
        effectiveFees: mergeFees(costsData.fees, bedSelector.selector.feeOverrides),
      };
    }
  }

  return {
    effectiveCosts: costsData.costs,
    effectiveFees: costsData.fees,
  };
}
